import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/components/feature/ToastProvider";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams();
  const { showToast } = useToast();
  const { isFavorite, toggleFavorite } = useFavorites();

  const item = movies.find((movie) => movie.id === id);

  usePageMeta(
    item ? `${item.title} - 影视详情 - 后入影视网` : "影视详情 - 后入影视网",
    item
      ? `《${item.title}》${item.year}年${item.region}${item.genres.join("、")}作品，由${item.director}执导，${item.actors.join("、")}主演。${item.summary}`
      : "后入影视网影视详情页",
    item ? `${item.title},${item.genres.join(",")},影视详情,后入影视` : undefined
  );

  if (!item) {
    return (
      <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-secondary-400">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          未找到该影视内容
        </h1>
        <p className="mt-2 text-sm text-secondary-500">
          该资源可能已下架或链接有误
        </p>
        <Link
          to="/"
          className="mt-5 flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const related = movies
    .filter(
      (movie) =>
        movie.id !== item.id &&
        movie.type === item.type &&
        movie.genres.some((genre) => item.genres.includes(genre))
    )
    .sort((a, b) => b.hot - a.hot)
    .slice(0, 6);

  const hotList = [...movies]
    .filter((movie) => movie.type === item.type)
    .sort((a, b) => b.hot - a.hot)
    .slice(0, 6);

  const handlePlay = (label: string) => {
    showToast(`《${item.title}》${label} 播放功能演示暂未对接`, "ri-play-circle-line");
  };

  const handleFavorite = () => {
    const next = toggleFavorite(item.id);
    showToast(next ? "已加入收藏（本地演示）" : "已取消收藏", next ? "ri-heart-fill" : "ri-heart-line");
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，可分享给好友", "ri-link");
    } catch {
      showToast("分享链接复制失败，请手动复制地址栏链接", "ri-error-warning-line");
    }
  };

  return (
    <div className="pb-16">
      <section className="relative overflow-hidden bg-primary-950">
        <img
          src={item.poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover object-center opacity-30 blur-2xl"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-950/70"></div>

        <div className="relative mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <div className="text-white/70 [&_a]:text-white/70 [&_a:hover]:text-accent-400 [&_span]:text-white">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: item.type === "movie" ? "电影" : "电视剧", to: `/category/${item.type}` },
                { label: item.title },
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-40 shrink-0 overflow-hidden rounded-lg md:w-56">
              <div className="aspect-[2/3] w-full">
                <img
                  src={item.poster}
                  alt={`${item.title} 主海报`}
                  title={`${item.title} 影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl font-extrabold text-white md:text-4xl">
                {item.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/75">
                <span className="flex items-center gap-1 font-semibold text-accent-400">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)} 分
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                <span>{item.type === "movie" ? "电影" : "电视剧"}</span>
                <span className="rounded bg-white/10 px-2 py-0.5">{item.status}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {item.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-md bg-white/10 px-3 py-1 text-xs text-white/85"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <dl className="mt-5 grid grid-cols-1 gap-2 text-sm text-white/75 sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-white/50">导演：</dt>
                  <dd>{item.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-white/50">主演：</dt>
                  <dd className="truncate">{item.actors.join(" / ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-white/50">更新时间：</dt>
                  <dd>{item.updatedAt}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => handlePlay(item.type === "movie" ? "正片" : "第01集")}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即播放
                </button>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-semibold transition-colors ${
                    favorited
                      ? "border-accent-400 bg-accent-500/20 text-accent-300"
                      : "border-white/30 text-white hover:bg-white/10"
                  }`}
                >
                  <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/30 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto mt-8 grid w-full grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-accent-500">
                <i className="ri-play-list-2-line"></i>
              </span>
              播放源 · 章节列表
            </h2>
            <p className="mt-2 text-xs text-secondary-500">
              以下为演示列表，点击仅提示功能说明，不提供真实影音播放
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {item.episodes.map((episode) => (
                <button
                  key={episode.id}
                  type="button"
                  onClick={() => handlePlay(episode.label)}
                  className="group flex flex-col items-start gap-1 rounded-md border border-background-200 bg-background-50 px-3 py-3 text-left transition-colors hover:border-accent-400 hover:bg-accent-50"
                >
                  <span className="flex w-full items-center gap-1.5 text-sm font-medium text-foreground-800 group-hover:text-accent-600">
                    <i className="ri-play-circle-line text-accent-500"></i>
                    {episode.label}
                  </span>
                  <span className="text-xs text-secondary-400">
                    {episode.duration}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-accent-500">
                <i className="ri-file-text-line"></i>
              </span>
              剧情简介
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              {item.summary}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              本页内容为演示数据，《{item.title}》由{item.director}执导，
              {item.actors.join("、")}领衔主演，
              是一部 {item.year} 年出品的{item.region}
              {item.type === "movie" ? "电影" : "电视剧"}作品，题材涵盖
              {item.genres.join("、")}。
            </p>
          </section>

          {related.length > 0 && (
            <section className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-accent-500">
                  <i className="ri-film-line"></i>
                </span>
                相关推荐
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                {related.map((movie) => (
                  <MovieCard key={movie.id} item={movie} />
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-1">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              所属分类
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={`/category/${item.type}`}
                className="whitespace-nowrap rounded-md bg-primary-50 px-3 py-1.5 text-sm text-primary-700 transition-colors hover:bg-primary-100"
              >
                {item.type === "movie" ? "全部电影" : "全部电视剧"}
              </Link>
              {item.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category/${item.type}?genre=${encodeURIComponent(genre)}`}
                  className="whitespace-nowrap rounded-md bg-accent-50 px-3 py-1.5 text-sm text-accent-700 transition-colors hover:bg-accent-100"
                >
                  {genre}
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              {item.type === "movie" ? "热门电影" : "热播剧集"}
            </h2>
            <ul className="mt-4 flex flex-col gap-3">
              {hotList.map((movie, index) => (
                <li key={movie.id}>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="group flex items-center gap-3"
                  >
                    <span
                      className={`w-5 text-center font-heading text-sm font-bold ${
                        index < 3 ? "text-accent-500" : "text-secondary-400"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="h-14 w-10 shrink-0 overflow-hidden rounded-md bg-background-100">
                      <img
                        src={movie.poster}
                        alt={`${movie.title} 海报`}
                        title={`${movie.title} 热门推荐`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-800 group-hover:text-accent-600">
                        {movie.title}
                      </span>
                      <span className="mt-0.5 block text-xs text-secondary-400">
                        {movie.year} · {movie.region}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}